import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, Mail, Calendar, AlertTriangle, Printer, ShieldCheck, Lock, Gift, Copy, Share2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  purple: "#5B2A86",
  purpleDeep: "#3F1A66",
  gold: "#F5C518",
  green: "#3FA437",
  cream: "#FAF6EC",
};

const MarketCampSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const ref = params.get("ref");
  const [status, setStatus] = useState("polling");
  const [reg, setReg] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Registration Confirmed · Mini Makers Market Camp";
    axios.get(`${API}/public/market-camp-settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setError("Missing session ID. If you were charged, please contact us with the time of your purchase.");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/public/market-camp/checkout-status`, { params: { session_id: sessionId } });
        if (cancelled) return;
        if (res.data?.status === "paid") {
          setReg(res.data.registration);
          setStatus("paid");
          return;
        }
        if (attempts >= 8) {
          setStatus("pending");
          setReg(res.data?.registration || null);
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        if (attempts >= 8) {
          setStatus("error");
          setError("Could not verify payment automatically. If you completed payment, please contact us with your reference code.");
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const whatsappPhone = settings?.whatsapp_phone || "17808632906";
  const code = reg?.registration_code || ref || "";
  const total = reg?.total ? reg.total.toFixed(2) : "";
  const isFree = (sessionId || "").startsWith("FREE-") || (reg && reg.total === 0);

  // Build pre-filled WhatsApp message including per-child ticket codes
  const ticketLines = (reg?.children || []).map((c, i) => {
    const tc = c.ticket_code || "—";
    return `  ${i + 1}. *${tc}* — ${c.full_name} (age ${c.age}${c.age_group ? ", grp " + c.age_group : ""})`;
  }).join("\n");
  const waMsg = encodeURIComponent(
    `🎉 *Mini Makers Market Camp* — Registration Confirmed!\n\n` +
    `Reg #: *${code}*\n\n` +
    `🎫 *MY TICKETS:*\n${ticketLines}\n\n` +
    `${settings ? `📅 ${settings.dates_label}\n⏰ ${settings.time_label}\n🎤 ${settings.showcase_label}\n📍 ${settings.location_label}\n\n` : ""}` +
    `💳 Total paid: $${total} CAD\n\n` +
    `Please confirm receipt — thank you!`
  );
  const waUrl = `https://wa.me/${whatsappPhone}?text=${waMsg}`;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: C.cream }} data-testid="market-camp-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-xl w-full bg-white rounded-3xl shadow-xl overflow-hidden"
        style={{ border: `2px solid ${C.purple}15` }}
      >
        <div className="px-6 sm:px-8 py-8 text-center" style={{ background: `linear-gradient(180deg, ${C.purple} 0%, ${C.purpleDeep} 100%)`, color: "white" }}>
          {status === "polling" && (
            <>
              <div className="w-14 h-14 mx-auto mb-4 border-4 border-white border-t-transparent rounded-full animate-spin" />
              <h1 className="text-2xl font-black mb-1">{isFree ? "Finalizing your registration…" : "Confirming payment…"}</h1>
              <p className="text-white/80 text-sm">{isFree ? "Hold tight — saving your spots." : "Hold tight while we verify with Stripe."}</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle2 className="w-12 h-12" style={{ color: C.green }} strokeWidth={2.5} />
              </div>
              <h1 className="text-2xl sm:text-3xl font-black mb-1">You're in! 🎉</h1>
              <p className="text-white/80 text-sm">Mini Makers Market Camp registration confirmed.</p>
            </>
          )}
          {status === "pending" && (
            <>
              <AlertTriangle className="w-14 h-14 mx-auto mb-3" style={{ color: C.gold }} />
              <h1 className="text-2xl font-black mb-1">Payment still processing</h1>
              <p className="text-white/80 text-sm">We're waiting on Stripe — feel free to reach out if it doesn't resolve soon.</p>
            </>
          )}
          {status === "error" && (
            <>
              <AlertTriangle className="w-14 h-14 mx-auto mb-3" style={{ color: C.gold }} />
              <h1 className="text-2xl font-black mb-1">Something went wrong</h1>
              <p className="text-white/80 text-sm">{error}</p>
            </>
          )}
        </div>

        {(status === "paid" || status === "pending") && (
          <div className="p-6 sm:p-8 space-y-4">
            {/* Verified Payment Banner */}
            {status === "paid" && !isFree && (
              <div
                className="rounded-2xl px-4 py-3 flex items-center gap-3"
                style={{ background: "linear-gradient(90deg, #0B2748 0%, #1F4E8A 100%)" }}
                data-testid="verified-payment-banner"
              >
                <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[2px] text-white/70 font-bold leading-none">Verified secure payment</p>
                  <p className="text-sm font-extrabold text-white leading-tight mt-0.5">Encrypted & confirmed by Stripe</p>
                </div>
                <span className="hidden sm:flex items-center gap-1 text-[10px] font-bold text-white/90 uppercase tracking-wider">
                  <Lock className="w-3 h-3" /> 256-bit
                </span>
              </div>
            )}
            {status === "paid" && isFree && (
              <div
                className="rounded-2xl px-4 py-3 flex items-center gap-3"
                style={{ background: "linear-gradient(90deg, #134E2A 0%, #2D7D26 100%)" }}
                data-testid="verified-free-banner"
              >
                <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[2px] text-white/70 font-bold leading-none">100% off · Coupon applied</p>
                  <p className="text-sm font-extrabold text-white leading-tight mt-0.5">Free registration confirmed — no payment required</p>
                </div>
              </div>
            )}

            <div className="rounded-2xl p-4 text-center" style={{ backgroundColor: C.cream, border: `2px dashed ${C.purple}40` }}>
              <p className="text-xs uppercase tracking-widest font-bold" style={{ color: C.purple }}>Registration Code</p>
              <p className="font-mono font-black text-2xl mt-1" style={{ color: C.purpleDeep }} data-testid="registration-code">{code || "—"}</p>
              {total && !isFree && <p className="text-sm text-gray-600 mt-1">Paid ${total} CAD</p>}
              {isFree && <p className="text-sm text-gray-600 mt-1">Total · Free (coupon)</p>}
            </div>

            {reg?.children?.length > 0 && (
              <div className="rounded-2xl p-4 space-y-3" style={{ backgroundColor: C.cream }}>
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-widest font-bold" style={{ color: C.purple }}>Your Ticket{reg.children.length > 1 ? "s" : ""}</p>
                  <button
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full text-white"
                    style={{ backgroundColor: C.purple }}
                    data-testid="print-tickets-btn"
                  >
                    <Printer className="w-3.5 h-3.5" /> Print tickets
                  </button>
                </div>
                {reg.children.map((c, i) => (
                  <div key={i} className="rounded-xl p-3 flex items-center gap-3" style={{ backgroundColor: "white", border: `2px solid ${C.purple}30` }} data-testid={`success-child-${i}`}>
                    <div className="flex-shrink-0 bg-white p-1 rounded border" style={{ borderColor: `${C.purple}30` }}>
                      <QRCodeSVG value={c.ticket_code || ""} size={84} level="M" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.purple }}>Ticket #{i + 1}</p>
                      <p className="font-mono font-black text-xl tracking-[2px]" style={{ color: C.purpleDeep }} data-testid={`success-ticket-${i}`}>{c.ticket_code || "—"}</p>
                      <p className="text-sm font-bold text-gray-800 mt-0.5">{c.full_name}</p>
                      <p className="text-xs text-gray-500">Age {c.age}{c.age_group ? ` · Group ${c.age_group}` : ""}</p>
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-gray-500 text-center italic">Show this email or screenshot at check-in on Day 1.</p>
              </div>
            )}

            {settings && (
              <div className="rounded-2xl p-4 flex items-start gap-3" style={{ backgroundColor: `${C.gold}18`, border: `2px solid ${C.gold}60` }}>
                <Calendar className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: C.purpleDeep }} />
                <div className="text-sm text-gray-800">
                  <p><strong>{settings.dates_label}</strong> · {settings.time_label}</p>
                  <p className="mt-1 text-xs">{settings.showcase_label}</p>
                  <p className="mt-1 text-xs italic">Parents/guardians: please attend the Showcase to cheer on your child!</p>
                </div>
              </div>
            )}

            {/* Refer-a-Friend Panel — only shown if registration has a share_code (paid status) */}
            {status === "paid" && reg?.share_code && settings?.referral_enabled !== false && (
              <ReferAFriendPanel reg={reg} settings={settings} />
            )}

            <a
              href={waUrl}
              target="_blank" rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-extrabold text-white shadow-lg hover:-translate-y-0.5 transition-all"
              style={{ backgroundColor: "#25D366" }}
              data-testid="whatsapp-confirm-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Send confirmation on WhatsApp
            </a>

            <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> A confirmation email is on its way to your inbox.
            </p>
            <p className="text-center text-[11px] text-gray-400 flex items-center justify-center gap-1.5" data-testid="stripe-secured-footer">
              <Lock className="w-3 h-3" /> Payment secured by Stripe · PCI-DSS Level 1 · 256-bit TLS
            </p>
            <div className="text-center">
              <Link to="/" className="text-sm font-bold underline" style={{ color: C.purple }}>← Back to homepage</Link>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="p-6 text-center space-y-3">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-extrabold text-white" style={{ backgroundColor: "#25D366" }}>
              <MessageCircle className="w-4 h-4" /> Contact us on WhatsApp
            </a>
            <div><Link to="/" className="text-sm font-bold underline" style={{ color: C.purple }}>Back to homepage</Link></div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default MarketCampSuccess;

/* ============================================================
   Refer-a-Friend Panel
   - Shows the parent their unique share code + WhatsApp/Copy share buttons
   - Pulls live "referred so far + credit pending" stats from /share/{code}
   ============================================================ */
const ReferAFriendPanel = ({ reg, settings }) => {
  const code = reg?.share_code || "";
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    axios.get(`${API}/public/market-camp/share/${code}`).then((r) => {
      if (!cancelled) setInfo(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [code]);

  const creditAmount = info?.credit_per_referral ?? settings?.referral_credit_amount ?? 20;
  const currency = (info?.currency || settings?.referral_credit_currency || "cad").toUpperCase();
  const shareUrl = `${window.location.origin}/mini-makers-market-camp?ref=${code}`;
  const template = info?.share_text_template || settings?.referral_share_text_template || "";
  const buildMessage = () => {
    const amtLabel = `$${creditAmount}`;
    if (!template) {
      return `Hey! My kid is going to Mini Makers Market Camp (${settings?.dates_label || "Aug 13–15"}, Edmonton). Use my code ${code} when you register — I get ${amtLabel} thank-you credit. Sign up: ${shareUrl}`;
    }
    return template
      .replace(/\{code\}/g, code)
      .replace(/\{url\}/g, shareUrl)
      .replace(/\{amount\}/g, amtLabel);
  };
  const msg = buildMessage();
  const waUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;

  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success("Copied!");
    } catch (e) {
      toast.error("Could not copy");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Mini Makers Market Camp", text: msg, url: shareUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      copy(msg);
    }
  };

  return (
    <div
      className="rounded-2xl p-5 overflow-hidden"
      style={{ background: "linear-gradient(135deg, #FFF7E6 0%, #FFEFC2 100%)", border: `2px solid ${C.gold}` }}
      data-testid="refer-a-friend-panel"
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
          style={{ background: C.gold, color: C.purpleDeep }}
        >
          <Gift className="w-6 h-6" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[2px] font-extrabold" style={{ color: C.purpleDeep }}>
            Earn ${creditAmount} per friend
          </p>
          <p className="text-base font-black leading-tight" style={{ color: C.purpleDeep }}>
            Share your code — get ${creditAmount} {currency} thank-you credit
          </p>
          <p className="text-[11px] text-gray-700 leading-tight mt-1">
            Every friend who registers using your code earns you ${creditAmount}. We&apos;ll send it to you via e-Transfer or your preferred method.
          </p>
        </div>
      </div>

      {/* Share Code Display */}
      <div
        className="rounded-xl p-3 mb-3 flex items-center gap-3 bg-white"
        style={{ border: `2px dashed ${C.purple}40` }}
      >
        <div className="flex-1 min-w-0 text-center">
          <p className="text-[9px] uppercase tracking-[2px] font-extrabold text-gray-500">Your Share Code</p>
          <p className="font-mono font-black text-2xl tracking-[4px]" style={{ color: C.purpleDeep }} data-testid="share-code-display">
            {code}
          </p>
        </div>
        <button
          onClick={() => copy(code)}
          className="px-3 py-2 rounded-lg text-xs font-extrabold inline-flex items-center gap-1 text-white"
          style={{ background: C.purple }}
          data-testid="copy-share-code-btn"
          aria-label="Copy code"
        >
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
      </div>

      {/* Share buttons */}
      <div className="grid grid-cols-2 gap-2">
        <a
          href={waUrl}
          target="_blank" rel="noopener noreferrer"
          className="py-2.5 rounded-xl font-extrabold text-xs sm:text-sm text-white inline-flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity"
          style={{ background: "#25D366" }}
          data-testid="share-whatsapp-btn"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
        <button
          type="button"
          onClick={nativeShare}
          className="py-2.5 rounded-xl font-extrabold text-xs sm:text-sm inline-flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity"
          style={{ background: C.purpleDeep, color: "white" }}
          data-testid="share-native-btn"
        >
          <Share2 className="w-4 h-4" /> Share / Copy
        </button>
      </div>

      {/* Stats */}
      {info && (info.referred_count > 0 || info.pending_count > 0 || info.paid_count > 0) && (
        <div
          className="mt-3 rounded-xl p-3 flex items-center justify-around text-center"
          style={{ background: "rgba(91,42,134,0.08)" }}
          data-testid="referral-stats"
        >
          <div>
            <p className="text-xl font-black" style={{ color: C.purpleDeep }}>{info.referred_count}</p>
            <p className="text-[9px] uppercase tracking-wider font-bold text-gray-600">Friends Joined</p>
          </div>
          <div className="w-px h-8 bg-purple-300/40" />
          <div>
            <p className="text-xl font-black" style={{ color: C.purple }}>${(info.pending_amount || 0).toFixed(0)}</p>
            <p className="text-[9px] uppercase tracking-wider font-bold text-gray-600">Pending</p>
          </div>
          <div className="w-px h-8 bg-purple-300/40" />
          <div>
            <p className="text-xl font-black" style={{ color: C.green }}>${(info.paid_amount || 0).toFixed(0)}</p>
            <p className="text-[9px] uppercase tracking-wider font-bold text-gray-600">Paid</p>
          </div>
        </div>
      )}
    </div>
  );
};
